import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Shared, reusable column-preference engine for every major TedOps table.
// - show/hide (mandatory columns can never be hidden)
// - drag reorder
// - resizable widths
// - pin/freeze left or right
// - reset to default
// Persisted per user + per company + per table on the server (user_prefs), with a
// localStorage mirror so defaults stay intact and it still works offline / pre-load.
// NOTE: presentation only — it never touches any backend data contract.

const DEFAULT_WIDTH = 160;

function _norm(p) {
  return JSON.stringify({
    order: p.order || [],
    visible: [...(p.visible || [])].sort(),
    widths: Object.keys(p.widths || {}).sort().reduce((a, k) => ({ ...a, [k]: p.widths[k] }), {}),
    pinned: { left: [...(p.pinned?.left || [])].sort(), right: [...(p.pinned?.right || [])].sort() },
    sort: p.sort || null,
  });
}
function sameLayout(a, b) { return _norm(a) === _norm(b); }

export function colWidth(col, widths) {
  return widths[col.key] || col.defaultWidth || DEFAULT_WIDTH;
}

// Compute sticky offsets + styling for pinned columns, in displayed order.
export function stickyStyles(displayed, widths, pinned) {
  const leftKeys = displayed.filter((c) => pinned.left?.includes(c.key)).map((c) => c.key);
  const rightKeys = displayed.filter((c) => pinned.right?.includes(c.key)).map((c) => c.key);
  const out = {};
  let acc = 0;
  for (const c of displayed) {
    if (leftKeys.includes(c.key)) {
      out[c.key] = { side: "left", offset: acc, last: c.key === leftKeys[leftKeys.length - 1] };
      acc += colWidth(c, widths);
    }
  }
  acc = 0;
  for (let i = displayed.length - 1; i >= 0; i--) {
    const c = displayed[i];
    if (rightKeys.includes(c.key)) {
      out[c.key] = { side: "right", offset: acc, first: c.key === rightKeys[0] };
      acc += colWidth(c, widths);
    }
  }
  return out;
}

export function cellStyle(col, widths, sticky) {
  const style = {};
  const w = widths[col.key] || col.defaultWidth;
  const pinnedW = colWidth(col, widths);
  const s = sticky[col.key];
  if (s) {
    style.width = pinnedW; style.minWidth = pinnedW; style.maxWidth = pinnedW;
    style.position = "sticky";
    style[s.side] = s.offset;
    style.zIndex = 3;
  } else if (w) {
    style.width = w; style.minWidth = w; style.maxWidth = w;
  }
  return style;
}

function defaultsFor(columns) {
  return {
    order: columns.map((c) => c.key),
    visible: columns.filter((c) => !c.defaultHidden).map((c) => c.key),
    widths: columns.reduce((a, c) => (c.defaultWidth ? { ...a, [c.key]: c.defaultWidth } : a), {}),
    pinned: {
      left: columns.filter((c) => c.defaultPin === "left").map((c) => c.key),
      right: columns.filter((c) => c.defaultPin === "right").map((c) => c.key),
    },
    sort: (columns.find((c) => c.defaultSort) || {}).defaultSort || null,
  };
}

// Reconcile a saved blob against the CURRENT column set (columns may have been
// added or removed since the user last saved). Mandatory columns are always visible.
function reconcile(columns, saved) {
  const keys = columns.map((c) => c.key);
  const def = defaultsFor(columns);
  if (!saved) return def;
  const keptOrder = (saved.order || []).filter((k) => keys.includes(k));
  const order = [...keptOrder];
  keys.forEach((k, i) => {
    if (order.includes(k)) return;
    const prev = keys.slice(0, i).reverse().find((p) => order.includes(p));
    order.splice(prev ? order.indexOf(prev) + 1 : order.length, 0, k);
  });
  const savedKnown = Array.isArray(saved.order) ? saved.order.filter((k) => keys.includes(k)) : [];
  const addedSinceSave = keys.filter((k) => !savedKnown.includes(k));
  const visibleSet = new Set((saved.visible || []).filter((k) => keys.includes(k)));
  // columns added since the user last saved inherit their own default visibility
  addedSinceSave.forEach((k) => { if (!columns.find((c) => c.key === k)?.defaultHidden) visibleSet.add(k); });
  columns.forEach((c) => { if (c.mandatory) visibleSet.add(c.key); });
  const widths = { ...def.widths };
  Object.entries(saved.widths || {}).forEach(([k, v]) => { if (keys.includes(k)) widths[k] = v; });
  const pinned = {
    left: (saved.pinned?.left || []).filter((k) => keys.includes(k)),
    right: (saved.pinned?.right || []).filter((k) => keys.includes(k)),
  };
  const sortable = (k) => { const c = columns.find((x) => x.key === k); return c && c.sortable !== false; };
  const sort = saved.sort && keys.includes(saved.sort.key) && sortable(saved.sort.key) && ["asc", "desc"].includes(saved.sort.dir)
    ? { key: saved.sort.key, dir: saved.sort.dir } : null;
  return { order, visible: [...visibleSet], widths, pinned, sort };
}

export function useColumnPrefs(tableId, columns) {
  const { user } = useAuth();
  const cid = user?.active_company_id;
  const storageKey = `htss_tblprefs_${cid || "x"}_${tableId}`;
  const def = useMemo(() => defaultsFor(columns), [columns]);
  const [prefs, setPrefs] = useState(def);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef(null);
  const skipSave = useRef(true);

  // Load: server first; localStorage as a pre-load/offline mirror.
  useEffect(() => {
    let active = true;
    skipSave.current = true;
    setLoaded(false);
    let local = null;
    try { local = JSON.parse(localStorage.getItem(storageKey)); } catch { /* ignore */ }
    if (local) setPrefs(reconcile(columns, local));
    api.get(`/me/table-prefs/${tableId}`)
      .then((r) => { if (active) setPrefs(reconcile(columns, r.data?.prefs)); })
      .catch(() => { if (active && !local) setPrefs(def); })
      .finally(() => { if (active) { setLoaded(true); setTimeout(() => { skipSave.current = false; }, 700); } });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, cid]);

  // Persist (debounced) on every change once loaded. When the layout equals the system
  // defaults we clear the server pref (DELETE) instead of saving a defaults blob, so a
  // user with no customisation always resolves to {prefs:null} and inherits live defaults.
  useEffect(() => {
    if (!loaded || skipSave.current) return;
    clearTimeout(saveTimer.current);
    if (sameLayout(prefs, def)) {
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
      saveTimer.current = setTimeout(() => { api.delete(`/me/table-prefs/${tableId}`).catch(() => {}); }, 500);
      return;
    }
    try { localStorage.setItem(storageKey, JSON.stringify(prefs)); } catch { /* ignore */ }
    saveTimer.current = setTimeout(() => {
      api.put(`/me/table-prefs/${tableId}`, prefs).catch(() => {});
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs, loaded]);

  const displayed = useMemo(() => {
    const vis = new Set(prefs.visible);
    columns.forEach((c) => { if (c.mandatory) vis.add(c.key); });
    return prefs.order.map((k) => columns.find((c) => c.key === k)).filter((c) => c && vis.has(c.key));
  }, [prefs.order, prefs.visible, columns]);

  const sticky = useMemo(() => stickyStyles(displayed, prefs.widths, prefs.pinned), [displayed, prefs.widths, prefs.pinned]);

  const apply = useCallback((next) => {
    setPrefs((p) => {
      const vis = new Set(next.visible ?? p.visible);
      columns.forEach((c) => { if (c.mandatory) vis.add(c.key); });
      return { order: next.order ?? p.order, visible: [...vis], widths: next.widths ?? p.widths, pinned: next.pinned ?? p.pinned, sort: next.sort !== undefined ? next.sort : p.sort };
    });
  }, [columns]);

  const setSort = useCallback((key, dir) => {
    setPrefs((p) => ({ ...p, sort: dir ? { key, dir } : null }));
  }, []);

  const applyAll = useCallback((p) => {
    setPrefs(reconcile(columns, p));
  }, [columns]);

  const setWidth = useCallback((key, px) => {
    setPrefs((p) => ({ ...p, widths: { ...p.widths, [key]: Math.max(60, Math.round(px)) } }));
  }, []);

  const reset = useCallback(() => {
    skipSave.current = true;
    clearTimeout(saveTimer.current);
    setPrefs(def);
    api.delete(`/me/table-prefs/${tableId}`).catch(() => {});
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    setTimeout(() => { skipSave.current = false; }, 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def, tableId, storageKey]);

  return { prefs, loaded, displayed, sticky, apply, setWidth, setSort, applyAll, reset };
}
